<script lang="ts" setup>
import type { FiltersFormModel } from '../../../runtime/services/FilterService';
import FilterService, { parseContent } from '../../../runtime/services/FilterService';
import DeleteButton from './DeleteButton.vue';

const canManage = defineModel<boolean>('manage');

const { $alert } = useAlerts();
const { $t } = useLocalization();

const filter = inject<FormType<any>>('filter');
const routeKey = inject<ComputedRef<string>>('routeKey');
const components = inject<Ref<any[]>>('components');
const visible = inject<Ref<string[]>>('visible');
const reset = inject<() => void>('reset');
const apply = inject<() => void>('apply');

const loaded = ref(false);
const currentFilter = computed(() => getFilterState());

const definitions = ref([] as Resource<FiltersFormModel>[]);
const globalDefinitions = computed(() =>
{
    return definitions.value.filter(item => item.result.filterType === 'Global');
});
const privateDefinitions = computed(() =>
{
    return definitions.value.filter(item => item.result.filterType === 'Private');
});

onMounted(async () =>
{
    await loadTemplates();
});

async function onChange()
{
    await loadTemplates();
}

async function loadTemplates()
{
    try
    {
        const response = await FilterService.getList(routeKey.value);

        definitions.value = response.items;
        canManage.value = response.meta.canManage;
    }
    catch
    {
        $alert.danger($t('[[[Nie udało się pobrać listy filtrów]]]'));
    }
    finally
    {
        loaded.value = true;
    }
}

function isActive(item: Resource<FiltersFormModel>)
{
    return item.result.id === currentFilter.value.activeTemplate.id;
}

function applyFilter(item: Resource<FiltersFormModel>)
{
    reset();

    const content = parseContent(item.result.contentJson || '{}');

    filter.withData(content.form);
    visible.value = content.visible;

    setFilterState({
        activeTemplate: item.result,
        visible: content.visible,
        form: filter
    });

    apply();
}

async function deleteFilter(item: Resource<FiltersFormModel>)
{
    try
    {
        await FilterService.delete(item.result.id);

        $alert.success($t('[[[Filtr został usunięty]]]'));

        reset();

        currentFilter.value.activeTemplate = { id: 0 };
        visible.value = components.value.where(p => p.visible).select(p => p.name).toArray();

        apply();

        await onChange();
    }
    catch
    {
        $alert.danger($t('[[[Nie udało się usunąć szablonu]]]'));
    }
}

defineExpose({
    reload: loadTemplates
});
</script>

<template>
    <Portal to="filter-section">
        <!-- Default filters -->
        <CategoriesList
            v-if="globalDefinitions.length"
            :title="$t('[[[Domyślne filtry]]]')"
            :nodes="globalDefinitions"
            :active="(node: any) => isActive(node)"
            :toggle="(node: any) => applyFilter(node)"
        >
            <template #left>
                <i class="far fa-fw fa-filter" />
            </template>
            <template #default="{ node }">
                {{ node.result.name }}
            </template>
            <template v-if="canManage" #right="{ node }">
                <DeleteButton v-if="isActive(node)" :item="node" @click="deleteFilter" />
            </template>
        </CategoriesList>
        <!-- Private filters -->
        <CategoriesList
            v-if="privateDefinitions.length"
            :title="$t('[[[Moje filtry]]]')"
            :nodes="privateDefinitions"
            :active="(node: any) => isActive(node)"
            :toggle="(node: any) => applyFilter(node)"
        >
            <template #left>
                <i class="far fa-fw fa-filter" />
            </template>
            <template #default="{ node }">
                {{ node.result.name }}
            </template>
            <template #right="{ node }">
                <DeleteButton v-if="isActive(node)" :item="node" @click="deleteFilter" />
            </template>
        </CategoriesList>
    </Portal>
</template>
